/**
 * MessageBubble Component
 * Displays a single chat message with markdown support
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Bot,
  User as UserIcon,
  Copy,
  Check,
  Download,
  Brain,
  ChevronDown,
  ChevronRight,
  File as FileIcon,
  UserPlus,
  Flag,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '../../types';
import { AvatarMedia } from './AvatarMedia';
import { ImagePreviewModal } from './ImagePreviewModal';
import { parseAgentResponse } from '../../utils/agentResponse';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { MermaidDiagramCard } from './MermaidDiagramCard';
import { MessageMemoryFeedback } from './MessageMemoryFeedback';
import { ToolConfirmationCard } from './ToolConfirmationCard';
import { GeneratedImageSkeleton } from './GeneratedImageSkeleton';
import { splitProgressFromReasoning } from './messageProgress';
import {
  getChatAttachmentBlob,
  getGeneratedImageDownloadBlob,
  isAuthenticatedChatAttachmentUrl,
} from '../../services/api';
import { ChatActionConfirmation } from '../../types';

interface MessageBubbleProps {
  message: ChatMessage;
  agentName?: string;
  agentAvatar?: string;
  agentAvatarType?: 'image' | 'video';
  /** Called when the agent avatar is clicked (opens agent info). */
  onAgentAvatarClick?: () => void;
  userAvatar?: string;
  userAvatarType?: 'image' | 'video';
  onReport?: (message: ChatMessage) => void;
  /** Position among the agent replies counted from the newest (0 = latest). */
  assistantOffsetFromEnd?: number;
  onConfirmAction?: (confirmation: ChatActionConfirmation) => Promise<void> | void;
  onCancelAction?: (confirmation: ChatActionConfirmation) => Promise<void> | void;
}

type MessageAttachment = NonNullable<ChatMessage['attachments']>[number];

const MALFORMED_IMAGE_DATA_URL_PATTERN = /^data:image\/[a-z0-9.+-]+;base64,(https?:\/\/\S+)$/i;
const IMAGE_DATA_URL_PATTERN = /^data:image\/[a-z0-9.+-]+;base64,/i;
const IMAGE_URL_PATTERN =
  /^https?:\/\/\S+(?:\.(?:png|jpe?g|gif|webp|avif)(?:[?#]\S*)?|\/provider-outputs\/\S*|ai-gateway-outputs\.\S*)$/i;
const MARKDOWN_IMAGE_PATTERN = /!\[[^\]]*\]\((?:<([^>]+)>|([^)]+))\)/;

function markdownImageUrl(url: string): string {
  if (url.startsWith('data:image/')) {
    return url;
  }

  return `<${url.replace(/>/g, '%3E')}>`;
}

function normalizeAgentImageContent(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return content;

  if (/^!\[[^\]]*\]\(.+\)$/s.test(trimmed)) {
    return content;
  }

  const malformedDataUrl = trimmed.match(MALFORMED_IMAGE_DATA_URL_PATTERN);
  if (malformedDataUrl?.[1]) {
    return `![Generated image](${markdownImageUrl(malformedDataUrl[1])})`;
  }

  if (IMAGE_DATA_URL_PATTERN.test(trimmed) || IMAGE_URL_PATTERN.test(trimmed)) {
    return `![Generated image](${markdownImageUrl(trimmed)})`;
  }

  return content;
}

function extractDownloadableImageUrl(content: string): string | null {
  const trimmed = content.trim();
  if (!trimmed) return null;

  const markdownImage = trimmed.match(MARKDOWN_IMAGE_PATTERN);
  const markdownUrl = markdownImage?.[1] || markdownImage?.[2];
  if (markdownUrl) {
    return markdownUrl.trim();
  }

  const malformedDataUrl = trimmed.match(MALFORMED_IMAGE_DATA_URL_PATTERN);
  if (malformedDataUrl?.[1]) {
    return malformedDataUrl[1];
  }

  if (IMAGE_DATA_URL_PATTERN.test(trimmed) || IMAGE_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }

  return null;
}

function getImageDownloadFileName(messageId: string, url: string): string {
  const dataMimeType = url.match(/^data:(image\/[a-z0-9.+-]+);base64,/i)?.[1];
  const extensionFromMime = dataMimeType?.split('/')[1]?.replace('jpeg', 'jpg');
  const pathExtension = /^https?:\/\//i.test(url)
    ? new URL(url).pathname.match(/\.([a-z0-9]+)$/i)?.[1]
    : undefined;
  const extension = extensionFromMime || pathExtension || 'png';
  return `generated-image-${messageId}.${extension}`;
}

function shouldProxyGeneratedImageDownload(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      /(?:^|\.)r2\.cloudflarestorage\.com$/i.test(parsed.hostname) &&
      parsed.pathname.startsWith('/provider-outputs/')
    );
  } catch {
    return false;
  }
}

function triggerDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  agentName,
  agentAvatar,
  agentAvatarType,
  onAgentAvatarClick,
  userAvatar,
  userAvatarType,
  onReport,
  assistantOffsetFromEnd,
  onConfirmAction,
  onCancelAction,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isReasoningOpen, setIsReasoningOpen] = useState(false);
  const [attachmentObjectUrls, setAttachmentObjectUrls] = useState<Record<string, string>>({});
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    fileName: string;
  } | null>(null);
  const parsedContent = parseAgentResponse(message.content);
  const parsedReasoning = message.reasoning ? parseAgentResponse(message.reasoning) : null;
  const explicitReasoning =
    parsedReasoning?.reasoning?.trim() || parsedReasoning?.content.trim() || null;
  const parsedMessage = isUser
    ? { content: message.content, reasoning: null }
    : {
        content: parsedContent.content,
        reasoning: [explicitReasoning, parsedContent.reasoning]
          .filter((part): part is string => Boolean(part?.trim()))
          .filter((part, index, parts) => parts.indexOf(part) === index)
          .join('\n\n'),
      };
  const progressAwareReasoning = splitProgressFromReasoning(parsedMessage.reasoning);
  const displayContent = isUser
    ? parsedMessage.content
    : normalizeAgentImageContent(parsedMessage.content);
  const downloadableImageUrl = isUser ? null : extractDownloadableImageUrl(displayContent);
  const reasoning = progressAwareReasoning.reasoning;
  const latestProgressMessage = !isUser
    ? progressAwareReasoning.progressMessages[progressAwareReasoning.progressMessages.length - 1] ||
      null
    : null;
  const sources = isUser ? [] : message.sources || [];
  const attachments = useMemo(() => message.attachments || [], [message.attachments]);
  const isAnonymousLimitMessage =
    !isUser &&
    (Boolean(message.anonymousLimit) ||
      /^Guest .+ limit reached\.?$/i.test(displayContent.trim()) ||
      displayContent.trim().toLowerCase().includes('guest daily token limit reached'));
  const attachmentLoadKey = useMemo(
    () =>
      attachments
        .map((attachment) => `${attachment.publicId}:${attachment.fileUrl || ''}`)
        .join('|'),
    [attachments]
  );

  useEffect(() => {
    const imageAttachments = attachments.filter(
      (attachment) =>
        attachment.mimeType.startsWith('image/') &&
        isAuthenticatedChatAttachmentUrl(attachment.fileUrl)
    );

    if (imageAttachments.length === 0) {
      setAttachmentObjectUrls({});
      return;
    }

    let cancelled = false;
    const createdObjectUrls: string[] = [];
    setAttachmentObjectUrls({});

    void (async () => {
      const loadedUrls: Record<string, string> = {};
      for (const attachment of imageAttachments) {
        if (!attachment.fileUrl) continue;

        try {
          const blob = await getChatAttachmentBlob(attachment.fileUrl);
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          createdObjectUrls.push(objectUrl);
          loadedUrls[attachment.publicId] = objectUrl;
        } catch {
          // Keep the file tile visible even if preview fetch fails.
        }
      }

      if (!cancelled) {
        setAttachmentObjectUrls(loadedUrls);
      }
    })();

    return () => {
      cancelled = true;
      createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentLoadKey]);

  const handleCopy = () => {
    navigator.clipboard.writeText(displayContent || message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSignUp = () => {
    navigate('/', { state: { authMode: 'signup' } });
  };

  const downloadImage = async (url: string, fileName: string) => {
    if (isDownloadingImage) return;

    setIsDownloadingImage(true);

    try {
      if (url.startsWith('data:image/')) {
        triggerDownload(url, fileName);
        return;
      }

      const blob = shouldProxyGeneratedImageDownload(url)
        ? await getGeneratedImageDownloadBlob(url)
        : await fetch(url).then((response) => {
            if (!response.ok) {
              throw new Error(`Image download failed with status ${response.status}`);
            }
            return response.blob();
          });

      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, fileName);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch {
      // Keep the user on the current page if the provider URL cannot be fetched.
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const handleDownloadImage = async () => {
    if (!downloadableImageUrl) return;
    await downloadImage(
      downloadableImageUrl,
      getImageDownloadFileName(message.id, downloadableImageUrl)
    );
  };

  const getDisplayUrl = (attachment: MessageAttachment) => {
    const objectUrl = attachmentObjectUrls[attachment.publicId];
    if (objectUrl) return objectUrl;

    if (
      attachment.mimeType.startsWith('image/') &&
      isAuthenticatedChatAttachmentUrl(attachment.fileUrl)
    ) {
      return undefined;
    }

    return attachment.fileUrl || undefined;
  };

  const openAttachment = async (attachment: MessageAttachment) => {
    const url = getDisplayUrl(attachment);
    if (!url) return;

    if (attachment.mimeType.startsWith('image/')) {
      setSelectedImage({ url, fileName: attachment.fileName });
      return;
    }

    if (!isAuthenticatedChatAttachmentUrl(attachment.fileUrl)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const blob = await getChatAttachmentBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  };

  const downloadAttachment = async (attachment: MessageAttachment) => {
    const url = getDisplayUrl(attachment);
    if (!url || isDownloadingImage) return;

    setIsDownloadingImage(true);

    try {
      if (!isAuthenticatedChatAttachmentUrl(attachment.fileUrl)) {
        triggerDownload(url, attachment.fileName);
        return;
      }

      const blob = await getChatAttachmentBlob(url);
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, attachment.fileName);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch {
      // Keep the file tile visible if the download request fails.
    } finally {
      setIsDownloadingImage(false);
    }
  };

  // Format timestamp
  const formatTime = (date: Date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return messageDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return messageDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const avatarClassName = `hidden h-8 w-8 flex-shrink-0 items-center justify-center sm:flex overflow-hidden rounded-full ${
    isUser ? 'bg-blue-500 dark:bg-blue-600' : 'bg-gradient-to-br from-indigo-500 to-purple-600'
  }`;
  const avatarContent = isUser ? (
    <AvatarMedia
      src={userAvatar}
      type={userAvatarType}
      alt="You"
      fallback={<UserIcon size={16} className="text-white" />}
    />
  ) : (
    <AvatarMedia
      src={agentAvatar}
      type={agentAvatarType}
      alt="Agent"
      fallback={<Bot size={16} className="text-white" />}
    />
  );

  return (
    <>
      <div className={`mb-3 flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`flex max-w-full gap-2.5 sm:max-w-[70%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
        >
          {/* Avatar */}
          {isUser || !onAgentAvatarClick ? (
            <div className={avatarClassName}>{avatarContent}</div>
          ) : (
            <button
              type="button"
              onClick={onAgentAvatarClick}
              aria-label={agentName ?? 'Agent'}
              className={`${avatarClassName} cursor-pointer transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-indigo-400`}
            >
              {avatarContent}
            </button>
          )}

          {/* Message Content */}
          <div className={`flex min-w-0 flex-col ${isUser ? 'items-end' : 'items-start'}`}>
            {/* Message Bubble */}
            <div
              className={`rounded-lg px-3.5 py-2 ${
                isUser
                  ? 'bg-blue-500 text-white dark:bg-blue-600'
                  : 'bg-white text-gray-900 dark:bg-slate-800 dark:text-slate-100'
              }`}
            >
              {reasoning && (
                <div
                  className={
                    displayContent ? 'mb-2 border-b border-gray-100 pb-2 dark:border-slate-700' : ''
                  }
                >
                  <button
                    type="button"
                    onClick={() => setIsReasoningOpen((prev) => !prev)}
                    className="flex w-full items-center gap-1.5 text-left text-xs italic text-gray-400 transition-colors hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                  >
                    {isReasoningOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Brain size={13} />
                    <span>Reasoning</span>
                  </button>
                  {isReasoningOpen && (
                    <div className="mt-2 max-h-64 overflow-auto pr-1">
                      <MarkdownRenderer
                        text={reasoning}
                        className="text-xs italic leading-relaxed text-gray-400 dark:text-slate-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {message.pendingImage && <GeneratedImageSkeleton />}

              {!displayContent && !message.pendingImage && latestProgressMessage && (
                <div className="text-sm italic leading-relaxed text-gray-500 dark:text-slate-400">
                  {latestProgressMessage}
                </div>
              )}

              {displayContent && (
                <>
                  {isUser ? (
                    <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                      {displayContent}
                    </div>
                  ) : (
                    <MarkdownRenderer
                      text={displayContent}
                      sources={sources}
                      className="text-sm leading-relaxed"
                      onImageClick={({ src, alt }) =>
                        setSelectedImage({
                          url: src,
                          fileName: alt?.trim() || getImageDownloadFileName(message.id, src),
                        })
                      }
                      renderImageOverlay={
                        downloadableImageUrl
                          ? ({ src }) =>
                              src === downloadableImageUrl ? (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    void handleDownloadImage();
                                  }}
                                  disabled={isDownloadingImage}
                                  title={
                                    isDownloadingImage ? 'Downloading image' : 'Download image'
                                  }
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/65 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black/80 disabled:cursor-wait disabled:opacity-70"
                                >
                                  <Download size={16} />
                                </button>
                              ) : null
                          : undefined
                      }
                    />
                  )}
                </>
              )}

              {isAnonymousLimitMessage && (
                <div className="mt-3 border-t border-gray-100 pt-3 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={handleSignUp}
                    className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-teal-700"
                  >
                    <UserPlus size={14} />
                    Sign Up Free
                  </button>
                </div>
              )}

              {message.confirmation && (
                <ToolConfirmationCard
                  confirmation={message.confirmation}
                  onConfirm={onConfirmAction}
                  onCancel={onCancelAction}
                  isUser={isUser}
                />
              )}

              {message.diagrams && message.diagrams.length > 0 && (
                <div className="mt-3 space-y-3">
                  {message.diagrams.map((diagram) => (
                    <MermaidDiagramCard key={diagram.id} diagram={diagram} isUser={isUser} />
                  ))}
                </div>
              )}

              {attachments.length > 0 && (
                <div className={displayContent ? 'mt-3 space-y-2' : 'space-y-2'}>
                  {attachments.map((attachment) => {
                    const isImage = attachment.mimeType.startsWith('image/');
                    const displayUrl = getDisplayUrl(attachment);
                    if (isImage && displayUrl) {
                      return (
                        <div key={attachment.publicId} className="relative inline-block max-w-full">
                          <button
                            type="button"
                            onClick={() => void openAttachment(attachment)}
                            title={`Preview ${attachment.fileName}`}
                            className="block max-w-full cursor-zoom-in text-left"
                          >
                            <img
                              src={displayUrl}
                              alt={attachment.fileName}
                              className="max-h-48 max-w-full rounded-md object-contain transition-opacity hover:opacity-90"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void downloadImage(displayUrl, attachment.fileName);
                            }}
                            title="Download image"
                            className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black/80 disabled:cursor-wait disabled:opacity-70"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      );
                    }

                    const content = (
                      <div
                        className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs ${
                          isUser
                            ? 'border-blue-300/60 bg-blue-400/30 text-white'
                            : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
                        }`}
                      >
                        <FileIcon size={14} />
                        <span className="max-w-[220px] truncate">{attachment.fileName}</span>
                        <span className={isUser ? 'text-blue-100' : 'text-gray-400'}>
                          {(attachment.fileSize / 1024).toFixed(1)} KB
                        </span>
                        <Download
                          size={14}
                          className={isUser ? 'text-blue-100' : 'text-gray-400'}
                        />
                      </div>
                    );

                    return displayUrl ? (
                      <button
                        type="button"
                        key={attachment.publicId}
                        onClick={() => void downloadAttachment(attachment)}
                        title={`Download ${attachment.fileName}`}
                        className="block text-left"
                      >
                        {content}
                      </button>
                    ) : (
                      <div key={attachment.publicId}>{content}</div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Timestamp + Copy */}
            <div
              className={`mt-1 flex items-center gap-1.5 px-1 text-xs text-gray-500 dark:text-slate-500 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
            >
              <span>
                {formatTime(message.timestamp)}
                {message.status === 'sending' && <span className="ml-1 italic"> • Sending...</span>}
                {message.status === 'failed' && (
                  <span className="ml-1 text-red-500"> • Failed</span>
                )}
              </span>
              <button
                onClick={handleCopy}
                title="Copy message"
                className="rounded p-0.5 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-700 dark:hover:text-slate-300"
              >
                {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
              </button>
              {!isUser && message.status !== 'sending' && message.status !== 'failed' && (
                <MessageMemoryFeedback
                  target={{
                    messageId: message.id,
                    sessionId: message.sessionId || undefined,
                    assistantOffsetFromEnd,
                  }}
                />
              )}
              {!isUser && onReport && (
                <button
                  onClick={() => onReport(message)}
                  title={t('feedback.chat.reportResponse')}
                  className="rounded p-0.5 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                >
                  <Flag size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <ImagePreviewModal
        open={Boolean(selectedImage)}
        src={selectedImage?.url || ''}
        alt={selectedImage?.fileName || 'Image preview'}
        onClose={() => setSelectedImage(null)}
        onDownload={
          selectedImage
            ? () => void downloadImage(selectedImage.url, selectedImage.fileName)
            : undefined
        }
        downloadDisabled={isDownloadingImage}
        downloadTitle={isDownloadingImage ? 'Downloading image' : 'Download image'}
      />
    </>
  );
};
